"use client";

import * as React from "react";
import { AUTH_CHANGED_EVENT, getAccessToken, getRole, getUser, type UserSession } from "./auth";

type Role = "admin" | "operator" | "staff" | "finance" | "user" | "agent";

type AuthSnapshot = {
  token: string;
  user: UserSession | null;
  role: Role;
  isAuthed: boolean;
};

function readAuthSnapshot(): AuthSnapshot {
  const token = getAccessToken();
  const user = getUser();
  const role = getRole();
  return {
    token,
    user,
    role,
    isAuthed: !!token && !!user,
  };
}

export function useAuthSession() {
  const [mounted, setMounted] = React.useState(false);
  const [snapshot, setSnapshot] = React.useState<AuthSnapshot>(() => readAuthSnapshot());

  const sync = React.useCallback(() => {
    setSnapshot(readAuthSnapshot());
  }, []);

  React.useEffect(() => {
    setMounted(true);
    sync();

    const onStorage = (e: StorageEvent) => {
      if (!e.key || e.key === "access_token" || e.key === "auth_user" || e.key === "user") sync();
    };

    window.addEventListener(AUTH_CHANGED_EVENT, sync);
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", sync);

    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, sync);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", sync);
    };
  }, [sync]);

  return {
    mounted,
    sync,
    ...snapshot,
  };
}
